import type { ReactElement, ReactNode } from "react";
import {
	EmptyState,
	EmptyStateType,
} from "@/components/Molecules/EmptyState/EmptyState";
import { useAppTranslation } from "@/hooks/use-app-translation";

export const TABLE_SEARCH_EMPTY_DESCRIPTION_KEY =
	"admin.shared.table.empty.search.description";

interface TableSearchEmptyStateProps {
	/** i18n key for the title. Ignored when `title` is provided. */
	titleKey?: string;
	/** i18n key for the description. */
	descriptionKey?: string;
	/** Literal title (Storybook / Figma). Takes precedence over `titleKey`. */
	title?: ReactNode;
	/** Literal description. Takes precedence over `descriptionKey`. */
	description?: ReactNode;
	className?: string;
}

/**
 * TableView Empty Search — design-system {@link EmptyState} with no-results graphic.
 *
 * @see Figma TableView Empty Search 4341:90653
 * @see Figma EmptyState Empty Search 622:6092
 */
export function TableSearchEmptyState({
	titleKey,
	descriptionKey = TABLE_SEARCH_EMPTY_DESCRIPTION_KEY,
	title: titleProp,
	description: descriptionProp,
	className,
}: TableSearchEmptyStateProps): ReactElement {
	const { t } = useAppTranslation();
	const title = titleProp ?? (titleKey ? t(titleKey) : t("common.noResults"));
	const description = descriptionProp ?? t(descriptionKey);

	return (
		<EmptyState
			type={EmptyStateType.EmptySearch}
			title={title}
			description={description}
			showAction={false}
			className={className}
			data-testid="table-search-empty-state"
		/>
	);
}
